import axios from 'axios';
import { VERSION } from 'src/configs/featuresFlags';

export interface BasicOption<T = number> {
  label: string;
  value: T;
}

export interface InfoAutoResponse {
  id: number;
  list_price: boolean;
  name: string;
  prices: boolean;
  prices_from: number;
  prices_to: number;
  summary: string;
}

const BASE = `${process.env.REACT_APP_INFOAUTO_URL}`;

export async function getBrands() {
  const brands = await getFromInfoauto(
    `${process.env.REACT_APP_INFOAUTO_URL}/brands?`
  );

  const seen = new Set<number>();
  const responseData = brands
    .filter((brand: any) => {
      if (!brand.prices || seen.has(brand.id)) return false;
      seen.add(brand.id);
      return true;
    })
    .map((brand: any) => ({ value: brand.name, label: brand.name, id: brand.id }));

  return responseData;
}

export async function getModels(
  brand: number | undefined,
  year?: number | undefined
) {
  let requestUrl = `${process.env.REACT_APP_INFOAUTO_URL}/models?brand_id=${brand}&`;
  if (year) {
    requestUrl += `year=${year}&`;
  }

  const models = await getFromInfoauto(requestUrl);
  const seenModels = new Set<number>();
  const dedup = (model: any) => {
    if (seenModels.has(model.id)) return false;
    seenModels.add(model.id);
    return true;
  };
  const toOption = (model: any) => ({ value: model.name, label: model.name, id: model.id });

  if (VERSION.isLegacy) {
    return models.filter((m: any) => Boolean(m.prices) && dedup(m)).map(toOption);
  } else {
    if (year !== undefined) {
      return models
        .filter(
          (m: any) =>
            m.prices === true &&
            m.prices_from <= year &&
            year <= m.prices_to &&
            dedup(m)
        )
        .map(toOption);
    }
  }
}

export async function getVersions(brand: number, group: number, year?: number) {
  const versions = await getFromInfoauto(
    `${process.env.REACT_APP_INFOAUTO_URL}/versions?brand_id=${brand}&group_id=${group}&year=${year}&`
  );
  const seenVersions = new Set<number>();
  const responseData = versions
    .filter((version: any) => {
      if (!version.prices || seenVersions.has(version.codia)) return false;
      seenVersions.add(version.codia);
      return true;
    })
    .map((version: any) => ({
      id: version.codia,
      value: version.description,
      label: version.description,
      years: generateYears(version.prices_from, version.prices_to),
      codia: version.codia,
    }));

  return responseData;
}

export async function getPrice(
  codia: number,
  year: number,
  km: number,
  configId: string
) {
  const clientId = configId;
  const res = await axios.post(
    `${process.env.REACT_APP_INFOAUTO_URL}/prices${
      (clientId && `?config_id=${clientId}`) || ''
    }`,
    {
      codia,
      year,
      km,
    }
  );

  const price = {
    minimumPrice: res.data.min,
    maximumPrice: res.data.max,
    averagePrice: res.data.prom,
  };

  return price;
}

export async function getYearsFromBrand(id: number): Promise<BasicOption[]> {
  const url = `${BASE}/models?brand_id=${id}&`;

  const rawYears: InfoAutoResponse[] = await getFromInfoauto(url);

  let min: number[] = [];
  let max: number[] = [];

  let years: BasicOption[] = [];

  const filteredRawYears = rawYears.filter(
    (brand) => brand.prices_from && brand.prices_to
  );

  filteredRawYears.forEach((year) => {
    min.push(year.prices_from);
    max.push(year.prices_to);
  });

  years = generateYears(Math.min(...min), Math.max(...max));

  return years;
}

async function getFromInfoauto(url: string): Promise<any> {
  let page = 1;
  let arrayData: any = [];

  const firstRes = await axios.get(`${url}page=${page}&page_size=99`);
  let arrayCounter = firstRes.data.length;
  arrayData.push(...firstRes.data);

  while (arrayCounter === 99) {
    page++;
    const res = await axios.get(`${url}page=${page}&page_size=99`);
    arrayCounter = res.data.length;
    arrayData.push(...res.data);
  }

  return arrayData;
}

function generateYears(yearFrom: number, yearTo: number): BasicOption[] {
  let years = [];

  for (let i = yearFrom; i <= yearTo; i++) {
    years.push({
      value: i,
      label: String(i),
    });
  }

  return years;
}
